import React, {useEffect, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import {Spinner} from './PipelineParts.tsx'
import IconClose from '../../assets/icons/close.svg?react'

// Side panel frame shared by the Activation and Conversion details: overlay,
// close button (focused on open), Esc to close unless a dialog is on top,
// scrolling body and an optional fixed footer.
export const DetailPanelShell: React.FC<{
    entityKey: string
    titleId: string
    isRefreshing: boolean
    onClose: () => void
    // The row with the close button (destinations and dates).
    topLine: React.ReactNode
    title: React.ReactNode
    // The rest of the header, under the title.
    header?: React.ReactNode
    footer?: React.ReactNode
    width?: number
    children: React.ReactNode
}> = ({entityKey, titleId, isRefreshing, onClose, topLine, title, header, footer, width = 480, children}) => {
    const {t} = useTranslation('dashboard')
    const closeButton = useRef<HTMLButtonElement>(null)

    useEffect(() => { closeButton.current?.focus() }, [entityKey])

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape' || e.defaultPrevented || document.querySelector('dialog[open]')) return
            onClose()
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [onClose])

    return (
        <div className="fixed inset-0 z-40">
            <div onClick={onClose} aria-hidden="true" className="absolute inset-0 bg-neutralBlack-900/20"/>
            <aside role="dialog" aria-modal="true" aria-labelledby={titleId} style={{width}}
                   className="absolute top-0 right-0 bottom-0 max-w-full bg-white border-l border-neutralBlack-200 shadow-2xl flex flex-col">
                <div className="flex-1 min-h-0 overflow-y-auto">
                    <div className="px-6 pt-[22px] pb-5 border-b border-neutralBlack-100">
                        <div className="flex items-center gap-1.5">
                            {topLine}
                            <span className="flex-1"/>
                            {isRefreshing && <span className="text-neutralBlack-600" aria-label={t('pipelines.loading')}><Spinner/></span>}
                            <button ref={closeButton} type="button" onClick={onClose} aria-label={t('pipelines.panel.close')} title={t('pipelines.panel.close')}
                                    className="w-8 h-8 -mr-1.5 flex-none flex items-center justify-center rounded-full bg-neutralBlack-100 text-neutralBlack-700 hover:bg-neutralBlack-200 transition-colors duration-200 ease-out">
                                <IconClose className="size-3"/>
                            </button>
                        </div>
                        <h2 id={titleId} className="mt-3 text-[24px] font-bold tracking-[-.02em] text-neutralBlack-900">{title}</h2>
                        {header}
                    </div>
                    {children}
                </div>
                {footer && (
                    <div className="flex-none px-6 py-3.5 border-t border-neutralBlack-200 bg-white flex items-center gap-2 flex-wrap">
                        {footer}
                    </div>
                )}
            </aside>
        </div>
    )
}
